import { AIIcon } from '@modules/scoring/components/ScoringScoreBadge';
import { isDevEnv } from '@helper/envs';
import { Roles } from '@utils/roleUtils';

export type DashboardMenuIconKey =
    | 'blog'
    | 'dashboard'
    | 'jobManagement'
    | 'premiumOption'
    | 'talentList'
    | 'companies'
    | 'organization'
    | 'pricingTable'
    | 'subscriptionPlans'
    | 'roleManagement';

export interface ProfileMenuItem {
    id: string;
    title: string;
    icon: string;
    path?: string;
}

export interface DashboardMenuItem {
    id: string;
    title: string;
    path: string;
    permission?: string;
    /**
     * flaticon interface such as fi-rr-apps, fi-rr-job, fi-rr-premium, fi-rr-talent, fi-rr-companies, fi-rr-organization, fi-rr-price-tag, fi-rr-user
     */
    icon?: string | React.ReactNode;
    /**
     * Whether the parent item renders as a link in the Sidebar.
     * Note: Some existing Sidebar items intentionally omit/disable this.
     */
    isLink?: boolean;
    subItems?: DashboardSubMenuItem[];
}

export interface DashboardSubMenuItem {
    id: string;
    title: string;
    path: string;
    isLink: boolean;
    permission?: string;
}

const DEV_MENU: DashboardMenuItem[] = [
    {
        id: 'dashboard',
        title: 'Tableau de bord',
        icon: 'fi-br-apps',
        isLink: true,
        path: '/',
    },
    {
        id: 'talents',
        title: 'Talents',
        icon: 'fi-rr-mode-portrait',
        path: '/talents',
        permission: 'talents:read',
        isLink: false,
        subItems: [
            {
                id: 'talents.list',
                title: 'Liste des talents',
                isLink: true,
                path: '/talents',
                permission: 'talents:read',
            },
            {
                id: 'talents.create',
                title: 'Créer un talent',
                isLink: true,
                path: '/talents/create',
                permission: 'talents:create',
            },
        ],
    },
    // talent favorites heart icon
    {
        id: 'talentFavorites',
        title: 'Mes Favoris',
        icon: 'fi-br-heart',
        path: '/talent-favorites',
        permission: 'talents:read',
        isLink: true,
    },
    {
        id: 'companies',
        title: 'Etablissements',
        icon: 'fi-br-building',
        permission: 'brands:read',
        isLink: true,
        path: '/companies',
        subItems: [
            {
                id: 'companies.list',
                title: 'Liste des établissements',
                isLink: true,
                path: '/companies',
                permission: 'brands:read',
            },
            {
                id: 'companies.create',
                title: 'Créer un établissement',
                isLink: true,
                path: '/companies/create',
                permission: 'brands:create',
            },
        ],
    },
    {
        id: 'brands',
        title: 'Marques',
        icon: 'fi-br-copyright',
        permission: 'brands:read',
        isLink: false,
        path: '/brands',
        subItems: [
            { id: 'brands.list', title: 'Liste des marques', isLink: true, path: '/brands', permission: 'brands:read' },
            {
                id: 'brands.create',
                title: 'Créer une marque',
                isLink: true,
                path: '/brands/create',
                permission: 'brands:create',
            },
        ],
    },
    {
        id: 'jobManagement',
        title: 'Gestion des offres',
        icon: 'fi-br-briefcase',
        permission: 'job-offers:read',
        path: '/job-offers',
        isLink: false,
        subItems: [
            {
                id: 'jobManagement.list',
                title: 'Liste des offres',
                isLink: true,
                path: '/job-offers',
                permission: 'job-offers:read',
            },
            {
                id: 'jobManagement.create',
                title: 'Créer une offre',
                isLink: true,
                path: '/job-offers/create',
                permission: 'job-offers:create',
            },
            {
                id: 'jobManagement.template',
                title: "Modèles d'offres",
                isLink: true,
                path: '/job-offers/templates',
                permission: 'job-offers:read',
            },
            {
                id: 'jobManagement.templateCreate',
                title: "Créer un modèle d'offre",
                isLink: true,
                path: '/job-offers/templates/create',
                permission: 'job-offers:create',
            },
        ],
    },
    {
        id: 'jobApplications',
        title: 'Candidatures',
        icon: 'fi-br-inbox',
        permission: 'job-applications:read',
        isLink: true,
        path: '/applications',
    },
    // {
    //     id: 'questionnaires',
    //     title: 'Questionnaires',
    //     icon: 'fi-br-form',
    //     isLink: true,
    //     path: '/questionnaires',
    // },
    {
        id: 'premiumOption',
        title: 'Option premium',
        icon: 'fi-br-magic-wand',
        isLink: true,
        path: '/premium-option',
        permission: 'premium-option:read',
    },
    {
        id: 'blog',
        title: 'Articles de blog',
        icon: 'fi-br-blog-text',
        permission: 'content:read',
        isLink: true,
        path: '/blog',
    },
    {
        id: 'pricingTables',
        title: 'Tableaux de tarifs',
        permission: 'analytics:read',
        isLink: true,
        icon: 'fi-br-usd-circle',
        path: '/pricing-tables',
    },
    {
        id: 'subscriptionPlans',
        title: "Plans d'abonnement",
        permission: 'brands:read',
        isLink: true,
        icon: 'fi-br-credit-card',
        path: '/subscription-plans',
    },
    {
        id: 'scoring',
        title: 'Scoring',
        icon: <AIIcon />,
        permission: 'scoring:read',
        isLink: true,
        path: '/scoring',
    },
];

const PUBLIC_MENU: DashboardMenuItem[] = [
    {
        id: 'blog',
        title: 'Articles de blog',
        icon: 'fi-br-blog-text',
        permission: 'content:read',
        isLink: true,
        path: '/blog',
    },
];

// Pro user menu - placeholder for now, will be expanded in future user stories
const PRO_MENU: DashboardMenuItem[] = [
    {
        id: 'dashboard',
        title: 'Dashboard',
        icon: 'fi-br-apps',
        isLink: true,
        path: '/pro/dashboard',
    },
];

export const filterDashboardMenuItemsByPermission = (
    items: DashboardMenuItem[],
    hasPermission: (permission: string) => boolean,
): DashboardMenuItem[] => {
    return items.reduce<DashboardMenuItem[]>((acc, item) => {
        const itemAllowed = !item.permission || hasPermission(item.permission);
        const filteredSubItems = item.subItems?.filter(
            subItem => !subItem.permission || hasPermission(subItem.permission),
        );
        const hasVisibleSubItems = Boolean(filteredSubItems && filteredSubItems.length > 0);

        if (!itemAllowed && !hasVisibleSubItems) {
            return acc;
        }

        acc.push({
            ...item,
            isLink: itemAllowed ? item.isLink : false,
            subItems: filteredSubItems,
        });

        return acc;
    }, []);
};

export const getDashboardMenuItems = (role?: Roles): DashboardMenuItem[] => {
    // Check the current path
    const isProPath = window.location.pathname.startsWith('/pro');

    // Pro users get their own menu
    if ((role && [Roles.PRO, Roles.COMPANY, Roles.FREELANCE].includes(role)) || isProPath) {
        return PRO_MENU;
    }

    // Upercut users get dev or public menu
    if (role === Roles.UPERCUT) {
        return isDevEnv ? DEV_MENU : PUBLIC_MENU;
    }

    // Default to public menu for unauthenticated or unknown roles
    return PUBLIC_MENU;
};

export const findDashboardMenuItemById = (id: string, role?: Roles): DashboardMenuItem | undefined => {
    return getDashboardMenuItems(role).find(item => item.id === id);
};

export const PROFILE_MENU_ITEMS: ProfileMenuItem[] = [
    {
        id: 'usersManagement',
        title: 'Gestion des utilisateurs',
        path: '/upercuts',
        icon: 'fi-br-users',
    },
    {
        id: 'rolesManagement',
        title: 'Gestion des rôles',
        icon: 'fi-br-user-shield',
        path: '/upercuts/roles',
    },
];
